import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from '@/components/shared';
import { List } from '@refinedev/antd';
import { NotificationsRes } from '@/api/types/notificationsType';
import {
  useNotificationFeed,
  useNotificationUnreadCount,
} from '@/hooks/useNotifications';
import {
  getNotificationAction,
  isNotificationNavigable,
} from '@/utils/getNotificationRoute';
import { buildRequestFromNotification } from '@/utils/mapRequestToNotification';
import { BellOutlined, CheckOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigation, useTranslation } from '@refinedev/core';
import { Alert, Button, Space } from 'antd';
import { ReviewUserModal } from '@/components/modals';
import { GetReq } from '@/api/types/getReq';
import { getColumns } from './columns';

const PAGE_SIZE = 10;

export const NotificationsList: React.FC = () => {
  const { translate } = useTranslation();
  const { push } = useNavigation();
  const { refetchUnreadCount } = useNotificationUnreadCount();
  const [page, setPage] = useState(1);
  const {
    notifications,
    isLoading,
    isRefetching,
    isError,
    refetch,
    markAsRead,
    markAllAsRead,
    isMarkingAllRead,
    totalCount,
    adminType,
  } = useNotificationFeed();
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<GetReq | undefined>();

  const columns = getColumns(translate);
  const hasUnread = notifications.some((notification) => !notification.is_read);

  const paginatedNotifications = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return notifications.slice(start, start + PAGE_SIZE);
  }, [notifications, page]);

  useEffect(() => {
    setPage(1);
  }, [notifications.length]);

  const handleRefresh = async () => {
    await Promise.all([refetch(), refetchUnreadCount()]);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleRowClick = async (notification: NotificationsRes) => {
    const action = getNotificationAction(notification, adminType);

    if (action.kind === 'none') {
      return;
    }

    await markAsRead(notification);

    if (action.kind === 'review-registration') {
      const request = buildRequestFromNotification(notification, {});

      if (!request) {
        return;
      }

      setSelectedRequest(request);
      setReviewModalOpen(true);
      return;
    }

    push(action.route);
  };

  return (
    <List
      title={
        <div className="flex gap-x-3">
          <BellOutlined />
          <h1>{translate('notifications.title')}</h1>
        </div>
      }
      canCreate={false}
      headerButtons={() => (
        <Space>
          {hasUnread && (
            <Button
              icon={<CheckOutlined />}
              onClick={handleMarkAllAsRead}
              loading={isMarkingAllRead}
            >
              {translate('notifications.markAllRead')}
            </Button>
          )}
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
            loading={isRefetching}
          >
            {translate('buttons.refresh')}
          </Button>
        </Space>
      )}
    >
      {isError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={translate('notifications.error.load')}
        />
      )}

      <DataTable
        pageSize={PAGE_SIZE}
        currentPage={page}
        onPageChange={setPage}
        columns={columns}
        loading={isLoading || isRefetching}
        data={{
          data: paginatedNotifications,
          headers: {
            'x-total-count': totalCount,
          },
        }}
        onRow={(record) => {
          const notification = record as unknown as NotificationsRes;
          const isInteractive = isNotificationNavigable(
            notification,
            adminType
          );

          return {
            onClick: () => handleRowClick(notification),
            onKeyDown: (event) => {
              if (
                isInteractive &&
                (event.key === 'Enter' || event.key === ' ')
              ) {
                event.preventDefault();
                handleRowClick(notification);
              }
            },
            tabIndex: isInteractive ? 0 : undefined,
            role: isInteractive ? 'button' : undefined,
            style: {
              cursor: isInteractive ? 'pointer' : 'default',
            },
          };
        }}
        rowClassName={(record) => {
          const notification = record as unknown as NotificationsRes;

          return !notification.is_read ? 'bg-[#fff2ef] dark:bg-[#241515]' : '';
        }}
      />

      <ReviewUserModal
        open={reviewModalOpen}
        userData={selectedRequest}
        onClose={() => {
          setReviewModalOpen(false);
          setSelectedRequest(undefined);
        }}
        refetch={async () => {
          await Promise.all([refetch(), refetchUnreadCount()]);
        }}
      />
    </List>
  );
};
